import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type PresignedResponse = { url: string; location: string; uploadToken: string };

async function uploadOne(conversationId: string, file: File): Promise<void> {
  const { data: presigned } = await api.post<PresignedResponse>(
    `conversations/${conversationId}/files/presigned-upload-url`,
    { fileName: file.name },
  );

  const s3Response = await fetch(presigned.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!s3Response.ok) {
    throw new Error(`Failed to upload to storage: ${s3Response.status} ${s3Response.statusText}`);
  }

  await api.post(`conversations/${conversationId}/files`, {
    uploadToken: presigned.uploadToken,
    fileSize: file.size,
    fileType: file.type,
  });
}

// TEMPORARY test helper for the Siesta chat-file upload backend. Uploads one or more files to the
// conversation's chat-file store in parallel via the presigned URL + upload-token flow.
export function useUploadChatFile(conversationId: string) {
  const { mutateAsync, isPending } = useMutation<void, Error, File[]>({
    mutationFn: async files => {
      const results = await Promise.allSettled(files.map(file => uploadOne(conversationId, file)));

      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          notifyAndLogError(
            'Upload failed',
            `Could not attach ${files[index].name}`,
            result.reason,
          );
        }
      });

      const succeeded = results.filter(result => result.status === 'fulfilled').length;
      if (succeeded > 0) {
        notifications.show({
          title: 'Files attached',
          message: `${succeeded} file(s) uploaded to this chat.`,
          color: 'green',
        });
      }
    },
  });

  return { uploadChatFiles: mutateAsync, isUploading: isPending };
}
